import { apiConfig } from 'config';
import { createApiPolling, logger } from '../common/utils/helpers';

const STATUS_CHECK_INTERVAL_MS = 2000;

export async function setDirection(params, options, version = 'v1', activeStatuses) {
  const callSetDirection = getSetDirectionCaller(version);

  const checkStatus = await callSetDirection(params, options);

  return waitUntilStatusCompleted(checkStatus, { delay: STATUS_CHECK_INTERVAL_MS }, activeStatuses);
}

function getSetDirectionCaller(version) {
  if (version === 'v2') {
    return callSetDirectionV2;
  }

  return callSetDirectionV1;
}

export async function callSetDirectionV1({ deviceId, direction }, { retry } = {}) {
  const url = apiConfig.cmServiceSetDirection(deviceId);
  const body = JSON.stringify({
    beamAzimuth: direction.azimuth,
    beamElevation: direction.elevation
  });

  return withErrorHandling(withRetry(request, retry))(url, {
    method: 'POST',
    body
  });
}

export function callSetDirectionV2({ deviceId, directions }, { retry } = {}) {
  const url = apiConfig.cmServiceSetDirectionV2(deviceId);
  const body = JSON.stringify(directions);

  return withErrorHandling(withRetry(request, retry))(url, {
    method: 'POST',
    body
  });
}

export function waitUntilStatusCompleted({ statusQueryGetUri }, options, activeStatuses) {
  return createApiPolling(
    withErrorHandling(request),
    (status) => status != null && !isActiveInstanceStatus(status, activeStatuses),
    options
  )(statusQueryGetUri);
}

function isActiveInstanceStatus(status, activeStatuses = ['Pending', 'Running']) {
  return activeStatuses.includes(status.runtimeStatus);
}

export function withRetry(requestFn, retry = 0) {
  let retryCount = retry;

  async function requestWithRetry(url, options) {
    try {
      return await requestFn(url, options);
    } catch (error) {
      if (retryCount > 0) {
        retryCount -= 1;

        const auth = window.__APP_AUTH;

        if (auth && error?.cause?.status === 420) {
          await auth.renewSession();

          return requestWithRetry(url, options);
        }
      }

      throw error;
    }
  }

  return requestWithRetry;
}

export function withErrorHandling(requestFn) {
  async function requestWithErrorHandling(url, options) {
    try {
      return await requestFn(url, options);
    } catch (error) {
      const history = window.__APP_HISTORY;

      if (history && error?.cause?.status === 420) {
        history.replace('/login');
      }

      throw error;
    }
  }

  return requestWithErrorHandling;
}

export async function request(url, options = {}) {
  const headers = { ...getDefaultHeaders(), ...options.headers };
  const response = await fetch(url, {
    method: 'GET',
    mode: 'cors',
    cache: 'no-cache',
    ...options,
    headers
  });

  if (response.ok) {
    return response.json();
  }

  let message = 'Request failed';

  try {
    message = await response.text();
  } catch (error) {
    logger.error(error);
  }

  throw new Error(message, { cause: response });
}

function getDefaultHeaders() {
  const auth = window.__APP_AUTH;

  return {
    Accept: 'application/json',
    Authorization: 'Bearer ' + auth?.accessToken,
    'Content-Type': 'application/json'
  };
}
